import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { ArrowLeft, PlusCircle, Upload } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  Hotel,
  Hotel as HotelBrand,
  PmsType,
  WIFI_SECURITY_OPTIONS,
  WifiSecurity,
} from "@/types/hotel";
import {
  HotelFormData,
  hotelSchema,
  hotelUpdateSchema,
} from "../utils/hotelSchema";
import { useHotelCreate, useHotelUpdate } from "../hooks/useHotels";
import { useCreateTenantScopedUser } from "@/components/pages/users/hooks/useUsers";
import {
  FormField,
  SelectField,
} from "@/components/pages/reservations/guests/components/form/FormFields";
import { UserProfile } from "@/types/user-profile";
import ImageSelector from "@/components/pages/reservations/guests/components/form/ImageSelector";
import { Role } from "@/types/user";
import MultiSelect from "@/components/common/MultiSelect";
import { cn } from "@/lib/utils";
import LabeledFileInput from "@/components/common/LabeledFileInput";
import RichTextInput from "@/components/common/RichTextInput";
import ChatSettingsForm from "./ChatSettingsForm";
import BillingSettingsForm from "./BillingSettingsForm";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";
import { useHotelSubscription } from "@/components/pages/hotels/hooks/useHotelSubscriptions";
import { useGetPmsProviders } from "@/components/pages/pms/hooks/pms.hooks";
import { CountryField } from "@/components/pages/reservations/guests/components/form/CountryField";
import { countries } from "@/utils/countries";
import { hasHotelSubscriptionFeature } from "@/utils/accessControl";
import { PMS_PROVIDER_LABEL_KEYS } from "@/utils/pmsProviderLabels";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { validateEmail } from "@/utils/validation";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

interface HotelFormProps {
  hotel?: HotelBrand;
  onBack?: () => void;
  managers?: UserProfile[];
  tenantId?: string;
  showBackButton?: boolean;
  showExtendedSections?: boolean;
  showManagersField?: boolean;
  showHotelDetailsSummary?: boolean;
  title?: string;
}

const PASSWORD_MIN_LENGTH = 8;
const MANAGER_ROLES = new Set<Role>([Role.HOTEL_ADMIN, Role.TENANT_ADMIN]);

const getCountryCodeValue = (country?: string): string => {
  const normalizedCountry = country?.trim();

  if (!normalizedCountry) {
    return "";
  }

  if (/^[A-Za-z]{2}$/.test(normalizedCountry)) {
    return normalizedCountry.toUpperCase();
  }

  return (
    countries.find(
      ({ name }) => name.toLowerCase() === normalizedCountry.toLowerCase(),
    )?.code || ""
  );
};

const formatManagerLabel = (manager: UserProfile): string => {
  const fullName = [manager.firstName, manager.lastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");

  if (fullName && manager.email) {
    return `${fullName} - ${manager.email}`;
  }

  return fullName || manager.email || manager.id;
};

const upsertManager = (
  managers: UserProfile[],
  manager: UserProfile,
): UserProfile[] => {
  const existingIndex = managers.findIndex((item) => item.id === manager.id);

  if (existingIndex === -1) {
    return [...managers, manager];
  }

  return managers.map((item, index) =>
    index === existingIndex ? manager : item,
  );
};

export const HotelForm: React.FC<HotelFormProps> = ({
  hotel,
  onBack,
  managers = [],
  tenantId,
  showBackButton = true,
  showExtendedSections = true,
  showManagersField = true,
  showHotelDetailsSummary = false,
  title,
}) => {
  const [policyText, setPolicyText] = useState<string | undefined>();
  const { t } = useTranslation();
  const { userProfile } = useAuthenticatedUser();
  const isEditing = !!hotel;
  const [selectedLogoFile, setSelectedLogoFile] = useState<File | undefined>(
    undefined,
  );
  const [selectedMapFile, setSelectedMapFile] = useState<File | undefined>(
    undefined,
  );
  const [createdManagers, setCreatedManagers] = useState<UserProfile[]>([]);
  const [isAddManagerOpen, setIsAddManagerOpen] = useState(false);
  const [managerEmail, setManagerEmail] = useState("");
  const [managerPassword, setManagerPassword] = useState("");
  const [managerFirstName, setManagerFirstName] = useState("");
  const [managerLastName, setManagerLastName] = useState("");

  const [currentMapFileId, setCurrentMapFileId] = useState<string | undefined>(
    hotel?.mapFileId,
  );
  const { capabilities } = useAccessCapabilities();
  const { data: hotelSubscription } = useHotelSubscription(
    hotel?.id ?? null,
    Boolean(hotel?.id),
    undefined,
    tenantId,
  );
  const { data: pmsProviders = [] } = useGetPmsProviders();
  const pmsOptions = useMemo(
    () =>
      pmsProviders.map((provider) => {
        const providerType = provider.type as PmsType;
        const labelKey = PMS_PROVIDER_LABEL_KEYS[providerType];
        return {
          value: providerType,
          label: labelKey ? t(labelKey) : provider.type,
        };
      }),
    [pmsProviders, t],
  );

  // Tenant mode (platform admins): requests and uploads go to the tenant of
  // `tenantId` through `x-tenant-id` (DV-168).
  const { handleHotelSubmit: handleHotelCreate, isLoading: isCreating } =
    useHotelCreate(tenantId);
  const { handleHotelSubmit: handleHotelUpdate, isLoading: isUpdating } =
    useHotelUpdate(hotel?.id || "", tenantId);
  const createManagerMutation = useCreateTenantScopedUser();
  const managerTenantId = tenantId ?? userProfile?.tenantId;
  const currentTenantAdminManager = useMemo<UserProfile | null>(() => {
    if (userProfile?.role !== Role.TENANT_ADMIN || !userProfile.profile?.id) {
      return null;
    }

    return {
      id: userProfile.profile.id,
      keycloakUserId: userProfile.openIdUserId,
      tenantId: userProfile.tenantId,
      role: Role.TENANT_ADMIN,
      email: userProfile.profile.email,
      firstName: userProfile.profile.firstName,
      lastName: userProfile.profile.lastName,
      phone: userProfile.profile.phone,
      photoId: userProfile.profile.photoId,
      preferredLanguage: userProfile.profile.preferredLanguage,
      createdAt: userProfile.createdAt,
      updatedAt: userProfile.updatedAt,
    };
  }, [userProfile]);
  const managerOptions = useMemo(() => {
    const uniqueManagers = new Map<string, UserProfile>();

    [currentTenantAdminManager, ...managers, ...createdManagers].forEach(
      (manager) => {
        if (!manager || !MANAGER_ROLES.has(manager.role as Role)) {
          return;
        }
        uniqueManagers.set(manager.id, manager);
      },
    );

    return Array.from(uniqueManagers.values());
  }, [createdManagers, currentTenantAdminManager, managers]);

  const isHotelSubscriptionResolved = Boolean(hotel?.id && hotelSubscription);
  const canUseGuestCommunication = isHotelSubscriptionResolved
    ? hasHotelSubscriptionFeature(
        hotelSubscription.features,
        "guest_communication",
      )
    : capabilities.canUseGuestCommunication;
  const canUseAiAssistant = isHotelSubscriptionResolved
    ? hasHotelSubscriptionFeature(hotelSubscription.features, "ai_assistant")
    : capabilities.canUseAiAssistant;
  const validationSchema = useMemo(
    () => (isEditing ? hotelUpdateSchema : hotelSchema),
    [isEditing],
  );

  const findManagers = (hotel?: Hotel) => {
    return (
      hotel?.employees?.filter((employee) =>
        MANAGER_ROLES.has(employee.role as Role),
      ) ?? []
    );
  };

  const getChatSettingsDefaults = useCallback(
    (chatSettings?: Partial<NonNullable<Hotel["chatSettings"]>>) => ({
      isAiEnabled: canUseAiAssistant
        ? (chatSettings?.isAiEnabled ?? true)
        : false,
      isTranslationEnabled:
        chatSettings?.isTranslationEnabled ?? canUseGuestCommunication,
      defaultLanguage: chatSettings?.defaultLanguage || "en",
    }),
    [canUseAiAssistant, canUseGuestCommunication],
  );

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm<HotelFormData>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      name: hotel?.name || "",
      logoId: hotel?.logoId || "",
      address: {
        street: hotel?.hotelAddress?.street || "",
        city: hotel?.hotelAddress?.city || "",
        state: hotel?.hotelAddress?.state || "",
        country: getCountryCodeValue(hotel?.hotelAddress?.country),
        postalCode: hotel?.hotelAddress?.postalCode || "",
        googlePlaceId: hotel?.hotelAddress?.googlePlaceId || "",
        googleMapsCid: hotel?.hotelAddress?.googleMapsCid || "",
      },
      pms: hotel?.pms || PmsType.MEWS,
      wifiNetwork: hotel?.wifiNetwork || "",
      wifiPassword: hotel?.wifiPassword || "",
      wifiSecurity: hotel?.wifiSecurity ?? WifiSecurity.WPA,
      mapFileId: hotel?.mapFileId || "",
      policyText: hotel?.policyText || "",
      chatSettings: getChatSettingsDefaults(hotel?.chatSettings),

      paymentSettings: {
        paymentEnabled: hotel?.paymentSettings?.paymentEnabled ?? false,
        currency: hotel?.paymentSettings?.currency,
      },

      managerIds: findManagers(hotel).map((manager) => manager.id),
    },
  });

  useEffect(() => {
    if (hotel) {
      setValue("name", hotel.name);
      setValue("logoId", hotel.logoId || "");
      setValue("mapFileId", hotel.mapFileId || "");
      setValue("policyText", hotel.policyText || "");
      setValue("address", {
        street: hotel.hotelAddress?.street || "",
        city: hotel.hotelAddress?.city || "",
        state: hotel.hotelAddress?.state || "",
        country: getCountryCodeValue(hotel.hotelAddress?.country),
        postalCode: hotel.hotelAddress?.postalCode || "",
        googlePlaceId: hotel.hotelAddress?.googlePlaceId || "",
        googleMapsCid: hotel.hotelAddress?.googleMapsCid || "",
      });
      setValue("pms", hotel.pms);
      setValue("wifiNetwork", hotel.wifiNetwork || "");
      setValue("wifiPassword", hotel.wifiPassword || "");
      setValue("wifiSecurity", hotel.wifiSecurity ?? WifiSecurity.WPA);
      setValue("chatSettings", getChatSettingsDefaults(hotel.chatSettings));
      setValue("paymentSettings", {
        paymentEnabled: hotel.paymentSettings?.paymentEnabled ?? false,
        currency: hotel.paymentSettings?.currency || "USD",
      });
      setValue(
        "managerIds",
        findManagers(hotel).map((manager) => manager.id),
      );
      setCurrentMapFileId(hotel.mapFileId);
      setPolicyText(hotel.policyText);
    }
  }, [getChatSettingsDefaults, hotel, setValue]);

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setValue("logoId", imageUrl);
      setSelectedLogoFile(file);
    }
  };

  const handleMapFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setValue("mapFileId", imageUrl);
      setSelectedMapFile(file);
      setCurrentMapFileId(undefined);
    }
  };

  const onSubmit = async (data: HotelFormData) => {
    try {
      data.policyText = policyText;
      data.chatSettings = {
        isAiEnabled: data.chatSettings?.isAiEnabled ?? false,
        isTranslationEnabled: data.chatSettings?.isTranslationEnabled ?? false,
        defaultLanguage: data.chatSettings?.defaultLanguage || "en",
      };
      const submitData = showManagersField ? data : { ...data, managerIds: [] };

      if (isEditing && hotel) {
        await handleHotelUpdate(
          submitData,
          selectedLogoFile,
          hotel.logoId,
          selectedMapFile,
          hotel.mapFileId,
        );
        toast.success(t("hotels.form.hotelUpdatedSuccessfully"));
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars, sonarjs/no-unused-vars
        const { paymentSettings: _, ...rest } = submitData;
        await handleHotelCreate(rest, selectedLogoFile, selectedMapFile);
        toast.success(t("hotels.form.hotelCreatedSuccessfully"));
      }
      onBack?.();
    } catch (error) {
      toast.error(
        isEditing
          ? t("hotels.form.failedToUpdateHotel")
          : t("hotels.form.failedToCreateHotel"),
      );
      console.error("Error saving hotel:", error);
    }
  };

  const managersIds = watch("managerIds") ?? [];
  const selectedImage = watch("logoId");
  const selectedCountry = watch("address.country");
  const pageTitle =
    title ||
    (isEditing ? t("hotels.form.editHotel") : t("hotels.form.addHotelBrand"));
  const notConfiguredLabel = t("hotels.form.notConfigured", "Not configured");
  const currentPlan = userProfile?.access?.currentPlan;
  const subscriptionPlanLabel =
    hotelSubscription && currentPlan?.id === hotelSubscription.planId
      ? currentPlan.title
      : (hotelSubscription?.planId ?? t("hotels.subscriptions.noPlan"));
  const timeZone =
    (hotel as (Hotel & { timeZone?: string; timezone?: string }) | undefined)
      ?.timeZone ??
    (hotel as (Hotel & { timeZone?: string; timezone?: string }) | undefined)
      ?.timezone;
  const checkInTime =
    (
      hotel as
        | (Hotel & { checkInTime?: string; checkinTime?: string })
        | undefined
    )?.checkInTime ??
    (
      hotel as
        | (Hotel & { checkInTime?: string; checkinTime?: string })
        | undefined
    )?.checkinTime;
  const checkOutTime =
    (
      hotel as
        | (Hotel & { checkOutTime?: string; checkoutTime?: string })
        | undefined
    )?.checkOutTime ??
    (
      hotel as
        | (Hotel & { checkOutTime?: string; checkoutTime?: string })
        | undefined
    )?.checkoutTime;
  const hotelClass = (hotel as (Hotel & { hotelClass?: string }) | undefined)
    ?.hotelClass;
  const hotelDetails = [
    {
      label: t("hotels.form.timeZone", "Time zone"),
      value: timeZone ?? notConfiguredLabel,
    },
    {
      label: t("hotels.form.checkInTime", "Check-in time"),
      value: checkInTime ?? notConfiguredLabel,
    },
    {
      label: t("hotels.form.checkOutTime", "Check-out time"),
      value: checkOutTime ?? notConfiguredLabel,
    },
    {
      label: t("hotels.form.hotelClass", "Hotel class"),
      value: hotelClass ?? notConfiguredLabel,
    },
    {
      label: t("hotels.form.numberOfRooms", "Number of rooms"),
      value:
        hotel?.rooms?.length !== undefined
          ? String(hotel.rooms.length)
          : notConfiguredLabel,
    },
    {
      label: t("hotels.form.subscriptionPlan", "Subscription / Plan"),
      value: hotelSubscription?.status
        ? `${subscriptionPlanLabel} (${hotelSubscription.status})`
        : subscriptionPlanLabel,
    },
  ];

  useEffect(() => {
    if (isEditing || managersIds.length > 0 || !currentTenantAdminManager) {
      return;
    }

    setValue("managerIds", [currentTenantAdminManager.id], {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [currentTenantAdminManager, isEditing, managersIds.length, setValue]);

  const resetManagerDialog = () => {
    setManagerEmail("");
    setManagerPassword("");
    setManagerFirstName("");
    setManagerLastName("");
  };

  const handleAddManagerOpenChange = (open: boolean) => {
    setIsAddManagerOpen(open);
    if (!open) {
      resetManagerDialog();
    }
  };

  const handleCreateManager = async (event: React.FormEvent) => {
    event.preventDefault();

    const email = managerEmail.trim();
    const password = managerPassword.trim();

    if (!managerTenantId) {
      toast.error(t("hotels.form.tenantRequired", "Tenant is required"));
      return;
    }

    if (!validateEmail(email).isValid) {
      toast.error(t("users.form.emailRequired", "Valid email is required"));
      return;
    }

    if (password.length < PASSWORD_MIN_LENGTH) {
      toast.error(t("users.form.passwordMinLength"));
      return;
    }

    try {
      const created = await createManagerMutation.mutateAsync({
        tenantId: managerTenantId,
        userData: {
          username: email,
          email,
          password,
          role: Role.HOTEL_ADMIN,
          ...(managerFirstName.trim()
            ? { firstName: managerFirstName.trim() }
            : {}),
          ...(managerLastName.trim()
            ? { lastName: managerLastName.trim() }
            : {}),
        },
      });

      const manager: UserProfile = {
        id: created.id,
        keycloakUserId: created.keycloakUserId,
        tenantId: managerTenantId,
        role: Role.HOTEL_ADMIN,
        email,
        firstName: managerFirstName.trim() || undefined,
        lastName: managerLastName.trim() || undefined,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      setCreatedManagers((current) => upsertManager(current, manager));
      setValue(
        "managerIds",
        Array.from(new Set([...managersIds, manager.id])),
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );
      toast.success(t("hotels.form.managerCreated", "User created"));
      handleAddManagerOpenChange(false);
    } catch (error) {
      toast.error(
        t("hotels.form.failedToCreateManager", "Failed to create user"),
      );
      console.error("Error creating hotel manager:", error);
    }
  };

  return (
    <div className={"p-12"}>
      {showBackButton && onBack ? (
        <Button onClick={onBack} variant="new_ghost" size="lg">
          <ArrowLeft />
          {t("hotels.form.back")}
        </Button>
      ) : null}
      <div className="mx-auto flex max-w-2xl flex-col gap-6">
        <h1 className="text-center text-2xl font-semibold text-zinc-900">
          {pageTitle}
        </h1>

        <form
          onSubmit={handleSubmit(onSubmit, (validationErrors) =>
            console.error("Form validation errors:", validationErrors),
          )}
          className="space-y-6"
        >
          <div className="flex flex-col items-center space-y-4">
            <ImageSelector
              defaultIcon={
                <div className="flex h-24 w-24 items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50"></div>
              }
              overlayContent={
                selectedImage ? (
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 rounded-full bg-white">
                    <PlusCircle className="size-5" />
                  </div>
                ) : (
                  <div
                    className={cn(
                      "absolute bottom-1/2 left-1/2 -translate-x-1/2 translate-y-1/2",
                    )}
                  >
                    <Upload className="h-8 w-8 text-gray-400" />
                  </div>
                )
              }
              onImageSelect={handleLogoUpload}
              selectedImage={selectedImage}
              tenantId={tenantId}
              className="mb-4"
              imageClassName="w-24 h-24 rounded-lg object-cover"
            />
          </div>

          <div className="grid grid-cols-2 gap-6">
            <FormField
              containerClassName={"col-span-2"}
              id="name"
              label={t("hotels.form.hotelName")}
              register={register}
              required={true}
              error={errors.name?.message}
              placeholder={t("hotels.form.enterHotelName")}
            />

            {showManagersField && (
              <div className={"col-span-2 w-full"}>
                <label className="text-sm font-medium">
                  {t("hotels.form.managers")}
                </label>
                <MultiSelect
                  containerClassName={"!min-w-full"}
                  options={managerOptions.map((manager) => ({
                    value: manager.id,
                    label: formatManagerLabel(manager),
                  }))}
                  value={managersIds}
                  onValueChange={(value) =>
                    setValue("managerIds", value, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                  placeholder={
                    managersIds.length
                      ? t("hotels.form.selectedUsers", {
                          count: managersIds.length,
                        })
                      : t("hotels.form.selectManagers")
                  }
                  className="!w-full !min-w-full"
                  footerAction={{
                    label: t("hotels.form.addUser", "Add user"),
                    onClick: () => handleAddManagerOpenChange(true),
                    icon: <PlusCircle className="h-4 w-4" />,
                    disabled: !managerTenantId,
                  }}
                />
                {errors.managerIds && (
                  <p className="text-sm text-red-600">
                    {errors.managerIds.message}
                  </p>
                )}
              </div>
            )}

            {showHotelDetailsSummary && (
              <div className="col-span-2 grid grid-cols-1 gap-3 rounded-lg border border-zinc-200 bg-zinc-50 p-4 sm:grid-cols-2">
                {hotelDetails.map((item) => (
                  <div key={item.label} className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-zinc-500">
                      {item.label}
                    </span>
                    <span className="text-sm font-semibold text-zinc-900">
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <FormField
              containerClassName={"col-span-2"}
              id="address.street"
              label={t("hotels.form.street")}
              register={register}
              required={true}
              error={errors.address?.street?.message}
              placeholder={t("hotels.form.enterStreet")}
            />

            <FormField
              id="address.city"
              label={t("hotels.form.city")}
              register={register}
              required={true}
              error={errors.address?.city?.message}
              placeholder={t("hotels.form.enterCity")}
            />

            <FormField
              id="address.state"
              label={t("hotels.form.state")}
              register={register}
              required={false}
              error={errors.address?.state?.message}
              placeholder={t("hotels.form.enterState")}
            />

            <CountryField
              selectedCountry={selectedCountry}
              onSelect={(code) =>
                setValue("address.country", code, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
              label={t("hotels.form.country")}
              required={true}
              error={errors.address?.country?.message}
              placeholder={t("hotels.form.selectCountry")}
            />

            <FormField
              id="address.postalCode"
              label={t("hotels.form.postalCode")}
              register={register}
              required={false}
              error={errors.address?.postalCode?.message}
              placeholder={t("hotels.form.enterPostalCode")}
            />

            <FormField
              id="address.googlePlaceId"
              label={t("hotels.form.googlePlaceId")}
              register={register}
              required={false}
              error={errors.address?.googlePlaceId?.message}
              placeholder={t("hotels.form.enterGooglePlaceId")}
            />

            <FormField
              id="address.googleMapsCid"
              label={t("hotels.form.googleMapsCid")}
              register={register}
              required={false}
              error={errors.address?.googleMapsCid?.message}
              placeholder={t("hotels.form.enterGoogleMapsCid")}
            />

            <FormField
              id="wifiNetwork"
              label={t("hotels.form.wifiNetwork")}
              register={register}
              required={true}
              error={errors.wifiNetwork?.message}
              placeholder={t("hotels.form.enterWifiNetworkName")}
            />

            <FormField
              id="wifiPassword"
              label={t("hotels.form.wifiPassword")}
              register={register}
              required={false}
              error={errors.wifiPassword?.message}
              placeholder={t("hotels.form.enterWifiPassword")}
            />

            <SelectField
              containerClassName="col-span-2"
              id="wifiSecurity"
              label={t("hotels.form.wifiSecurity")}
              required={true}
              options={WIFI_SECURITY_OPTIONS.map((option) => ({
                value: option.value,
                label: t(option.labelKey),
              }))}
              value={watch("wifiSecurity")}
              onValueChange={(value: WifiSecurity) =>
                setValue("wifiSecurity", value, { shouldDirty: true })
              }
              placeholder={t("hotels.form.selectWifiSecurity")}
              error={errors.wifiSecurity?.message}
              register={register}
            />

            <SelectField
              containerClassName="col-span-2"
              id="pms"
              label={t("hotels.form.pms")}
              required={true}
              options={pmsOptions}
              value={watch("pms")}
              onValueChange={(value: PmsType) => setValue("pms", value)}
              placeholder={t("hotels.form.selectPms")}
              error={errors.pms?.message}
              register={register}
            />

            <LabeledFileInput
              containerClassName="col-span-2"
              id="mapFileId"
              label={t("hotels.form.mapFile")}
              accept=".pdf"
              onChange={handleMapFileUpload}
              currentFileId={currentMapFileId}
              selectedFile={selectedMapFile}
              currentFileText={t("hotels.form.currentFileUploaded")}
              newFileSelectedText={t("hotels.form.newFileSelected")}
            />

            <RichTextInput
              value={policyText}
              dataColorMode="light"
              onChange={setPolicyText}
              containerClassName="col-span-2"
              label={t("hotels.form.policyText")}
            />

            {isEditing && showExtendedSections && (
              <>
                {hotel?.paymentSettings && (
                  <div className={"col-span-2"}>
                    <BillingSettingsForm control={control} />
                  </div>
                )}

                <div className="col-span-2">
                  <ChatSettingsForm
                    canUseAiAssistant={canUseAiAssistant}
                    canUseGuestCommunication={canUseGuestCommunication}
                    control={control}
                    errors={errors}
                  />
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end space-x-4">
            {onBack ? (
              <Button type="button" variant="outline" onClick={onBack}>
                {t("hotels.form.cancel")}
              </Button>
            ) : null}
            <Button
              type="submit"
              disabled={isSubmitting || isCreating || isUpdating}
            >
              {(() => {
                if (isSubmitting || isCreating || isUpdating) {
                  return t("hotels.form.saving");
                }
                if (isEditing) {
                  return t("hotels.form.updateHotel");
                }
                return t("hotels.form.createHotel");
              })()}
            </Button>
          </div>
        </form>

        <Dialog
          open={isAddManagerOpen}
          onOpenChange={handleAddManagerOpenChange}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {t("hotels.form.addManagerTitle", "Add manager")}
              </DialogTitle>
            </DialogHeader>

            <form
              onSubmit={handleCreateManager}
              className="flex flex-col gap-4"
            >
              <Input
                label={t("users.form.email")}
                type="email"
                value={managerEmail}
                onChange={(event) => setManagerEmail(event.target.value)}
                autoComplete="email"
                required
              />
              <Input
                label={t("users.form.password")}
                type="password"
                value={managerPassword}
                onChange={(event) => setManagerPassword(event.target.value)}
                autoComplete="new-password"
                minLength={PASSWORD_MIN_LENGTH}
                required
              />
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label={t("users.form.firstName")}
                  value={managerFirstName}
                  onChange={(event) => setManagerFirstName(event.target.value)}
                  autoComplete="given-name"
                />
                <Input
                  label={t("users.form.lastName")}
                  value={managerLastName}
                  onChange={(event) => setManagerLastName(event.target.value)}
                  autoComplete="family-name"
                />
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  disabled={createManagerMutation.isPending}
                  onClick={() => handleAddManagerOpenChange(false)}
                >
                  {t("common.cancel", "Cancel")}
                </Button>
                <Button
                  type="submit"
                  disabled={createManagerMutation.isPending}
                >
                  {createManagerMutation.isPending
                    ? t("common.saving", "Saving...")
                    : t("hotels.form.addUser", "Add user")}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};
